import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons/faTriangleExclamation';
import { TypedMemo } from '../../../common/types';
import { platformMeta } from '../platformMeta';
import { useChannelStats } from '../hooks/useChannelStats';
import { channelPostsThroughApi } from '../composer/settings/useComposerPlatformSettings';
import { SerializedSocialChannel, SocialLoginVariant, SocialPlatform } from '../types';
import { channelNumbersState, followersPillKind } from './channelNumbersState';
import { ChannelHandleForm } from './ChannelHandleForm';
import { ChannelCardActions } from './ChannelCardActions';
import { ChannelCardHead } from './ChannelCardHead';
import { ChannelConnectionIdentity } from './ChannelConnectionIdentity';
import { ChannelGrowthLine } from './ChannelGrowthLine';
import { ChannelNumbers } from './ChannelNumbers';
import { ChannelPostingDefaults } from './ChannelPostingDefaults';
import { ChannelStateLine } from './ChannelStateLine';
import { ChannelXPlanNote } from './ChannelXPlanNote';
import { ChannelXPosts } from './ChannelXPosts';
import { useMusic24Capabilities } from '../../hooks/useMusic24Capabilities';
import { xChannelNeedsPro } from '../xPostsLimit';
import { YouTubeConnectionNote } from '../../youtubeConnection/YouTubeConnectionNote';
import { Panel, PanelBody } from '../../components/primitives/Panel';
import { useUpgradePrompt } from '../../hooks/useUpgradePrompt';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { channelConnectNeedsPaidPlan, channelReconnectReason } from './channelConnectRoute';
/* eslint-disable css-modules/no-unused-class -- one stylesheet per screen; each part of the screen uses its own slice of it */
import styles from './PromoteChannels.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ChannelCardProps {
  accountId: number;
  channel: SerializedSocialChannel;
  /** The period of the growth line in days: 28, or 7 on Free. */
  periodDays: number;
  isSaving: boolean;
  connectedCount: number;
  onSaveHandle: (platform: SocialPlatform, handle: string) => void;
  onRemoveHandle: (platform: SocialPlatform) => void;
  /** Starts the connect flow. `loginVariant` is the second login of Instagram. */
  onConnect: (platform: SocialPlatform, loginVariant?: SocialLoginVariant) => void;
  onDisconnectConnection: (platform: SocialPlatform) => void;
}

const ChannelCardComponent: React.FC<ChannelCardProps> = ({
  accountId,
  channel,
  periodDays,
  isSaving,
  connectedCount,
  onSaveHandle,
  onRemoveHandle,
  onConnect,
  onDisconnectConnection,
}) => {
  const intl = useIntl();
  const meta = platformMeta[channel.platform];
  const [isEditing, setEditing] = useState(false);

  const startEditing = useCallback(() => setEditing(true), []);
  const reconnectYouTube = useCallback(() => onConnect(channel.platform), [channel.platform, onConnect]);
  const stopEditing = useCallback(() => setEditing(false), []);

  const saveHandle = useCallback(
    (handle: string) => {
      onSaveHandle(channel.platform, handle);
      setEditing(false);
    },
    [channel.platform, onSaveHandle]
  );

  const state = channelNumbersState(channel);
  // The growth line needs a read. After the sign-in ended, the last numbers stay.
  const hasNumbers = state === 'numbers' || state === 'reconnect';
  const stats = useChannelStats({ accountId, platform: channel.platform, days: periodDays, skip: !hasNumbers });
  const followerDays = useMemo(
    () => (stats.days ?? []).map((day) => day.followers).filter((value): value is number => value !== null),
    [stats.days]
  );

  const hasHandle = channel.handle !== null && channel.handle !== '';
  // The platform owns the handle of a connected channel, so it only reads here.
  const canEditHandle = hasHandle && channel.status !== 'connected';
  const pill = hasNumbers ? followersPillKind(channel) : 'hidden';
  const hasConnectionError = channel.connectionError !== null && channel.connectionError !== '';
  // An X login on a plan without X: the read stops, so the numbers are old and grey (design b2).
  const { canUseX } = useMusic24Capabilities();
  const needsPro = xChannelNeedsPro(channel, canUseX);
  // A Reconnect that goes to the posting partner on a plan with no partner login opens the plan
  // offer in place of the login (the server would answer 402 `canAutoPostToMeta`).
  const openUpgradePrompt = useUpgradePrompt();
  const needsPaidPlan = channelConnectNeedsPaidPlan(channel);
  const reconnect = useCallback(
    (platform: SocialPlatform) => {
      if (needsPaidPlan) {
        openUpgradePrompt(SMLUpgradeFeature.generic);
        return;
      }
      onConnect(platform);
    },
    [needsPaidPlan, onConnect, openUpgradePrompt]
  );
  // The server's own sentence why the channel must sign in again (a move off the partner).
  const reconnectReason = state === 'reconnect' ? channelReconnectReason(channel) : null;

  return (
    <Panel className={styles.card}>
      <ChannelCardHead channel={channel} canEditHandle={canEditHandle} pill={pill} onEditHandle={startEditing} />
      <PanelBody stack>
        {channel.connection === null ? null : (
          <ChannelConnectionIdentity
            connection={channel.connection}
            signedInWithInstagram={channel.status === 'connected' && channel.loginVariant === 'instagram'}
          />
        )}

        <ChannelNumbers channel={channel} state={state} dim={needsPro} />

        {hasNumbers && followerDays.length > 0 ? (
          <ChannelGrowthLine
            platform={channel.platform}
            values={followerDays}
            periodDays={periodDays}
            lastSyncedAt={channel.lastSyncedAt}
            dim={state === 'reconnect' || needsPro}
          />
        ) : null}

        {needsPro ? null : (
          <ChannelStateLine platform={channel.platform} state={state} disabled={isSaving} onReconnect={reconnect} />
        )}

        {/* The X posts of this month: only an X channel with a login on an X plan has the count. */}
        {channel.xPosts !== undefined && channel.xPosts !== null && !needsPro ? (
          <ChannelXPosts usage={channel.xPosts} fullNote="channels" />
        ) : null}

        {channel.platform === 'x' ? <ChannelXPlanNote channel={channel} disabled={isSaving} /> : null}

        {channel.platform === 'youtube' && channel.autoPublishAvailable ? (
          // One YouTube sign-in for Social and Streaming. A channel with no login keeps its Connect
          // button, so only a connected channel gets the Reconnect button.
          <YouTubeConnectionNote
            gap={channel.reconnectToAdd}
            showReconnect={channel.status === 'connected'}
            disabled={isSaving}
            onReconnect={reconnectYouTube}
            noteClassName={styles.quietNote}
            gapClassName={styles.warnNote}
          />
        ) : null}

        {/* Only a platform that may keep an answer (YouTube), on a channel that posts through the API. */}
        {channel.platform === 'youtube' && channelPostsThroughApi(channel) ? (
          <ChannelPostingDefaults accountId={accountId} channel={channel} />
        ) : null}

        {channel.autoPublishAvailable ? null : (
          <p className={styles.quietNote}>
            {intl.formatMessage(
              {
                id: 'app.sml.promote.channel.autoUnavailable',
                defaultMessage: 'Auto-posting is not available for {platform} yet. Save a handle and post it yourself.',
              },
              { platform: meta.name }
            )}
          </p>
        )}

        {reconnectReason === null ? null : (
          <p className={styles.warnNote}>
            <FontAwesomeIcon icon={faTriangleExclamation} /> {reconnectReason}
          </p>
        )}

        {/* The warn line "Reconnect" says why the sign-in ended, so the raw reason hides with it. */}
        {hasConnectionError && state !== 'reconnect' ? (
          <p className={styles.warnNote}>
            <FontAwesomeIcon icon={faTriangleExclamation} /> {channel.connectionError}
          </p>
        ) : null}

        {isEditing ? (
          <ChannelHandleForm
            meta={meta}
            initialHandle={channel.handle}
            isSaving={isSaving}
            onSave={saveHandle}
            onCancel={stopEditing}
          />
        ) : (
          <ChannelCardActions
            channel={channel}
            isSaving={isSaving}
            connectedCount={connectedCount}
            onEditHandle={startEditing}
            onRemoveHandle={onRemoveHandle}
            onConnect={onConnect}
            onDisconnectConnection={onDisconnectConnection}
          />
        )}
      </PanelBody>
    </Panel>
  );
};

export const ChannelCard = TypedMemo(ChannelCardComponent);
